import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Menu, X, ChevronDown, ChevronRight, ExternalLink,
  ArrowRight, Zap,
} from 'lucide-react';
import { CATEGORIES } from '../lib/categoryConfig';
import BrandLogo from './BrandLogo';

/* ─────────────────────────────────────────────────────────────────
   Constants
───────────────────────────────────────────────────────────────── */
const MOBILE_LINKS = [
  { label: 'Start',            path: '/' },
  { label: 'Om oss',           path: '/om-oss' },
  { label: 'Kontakt',          path: '/kontakt' },
  { label: 'Vår webbplats',    external: 'https://klarthemma.com' },
];

/* ─────────────────────────────────────────────────────────────────
   Services dropdown (desktop)
───────────────────────────────────────────────────────────────── */
function ServicesDropdown({ navigate, currentPath }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 140); };

  const isActive = CATEGORIES.some((c) => currentPath === c.route);

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`
          flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold
          transition-all duration-200
          ${isActive
            ? 'text-white bg-white/12'
            : 'text-white/70 hover:text-white hover:bg-white/10'
          }
        `}
      >
        Tjänster
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown panel */}
      <div
        className={`
          absolute left-0 top-full mt-2 w-56 z-50
          kh-dropdown-panel rounded-2xl border border-white/[0.09] bg-[#0d1117]/95 backdrop-blur-xl
          shadow-2xl shadow-black/50
          transition-all duration-200 origin-top-left
          ${open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}
        `}
      >
        <div className="p-1.5">
          {CATEGORIES.map((cat) => {
            const active = currentPath === cat.route;
            return (
              <button
                key={cat.slug}
                type="button"
                onClick={() => { navigate(cat.route); setOpen(false); }}
                className={`
                  w-full text-left px-3.5 py-2.5 rounded-xl text-[13px] font-medium
                  flex items-center justify-between group
                  transition-all duration-150
                  ${active
                    ? 'text-[#f5f2e9] bg-[#374ef4]/15'
                    : 'text-white/65 hover:text-white hover:bg-white/[0.07]'
                  }
                `}
              >
                {cat.label}
                <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-50 transition-opacity" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Mobile menu
───────────────────────────────────────────────────────────────── */
function MobileMenu({ open, onClose, navigate, currentPath }) {
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    if (!open) setServicesOpen(false);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (path) => { navigate(path); onClose(); };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div
        className={`
          fixed top-0 right-0 bottom-0 z-50 w-[min(88vw,340px)]
          kh-mobile-menu border-l border-white/[0.08]
          flex flex-col
          transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]
          ${open ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
          <Link to="/" onClick={onClose}>
            <BrandLogo />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Stäng meny"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">

          {/* CTA inside menu */}
          <div className="pb-4 mb-2 border-b border-white/[0.07]">
            <button
              type="button"
              onClick={() => go('/')}
              className="kh-button w-full px-4 py-3.5 text-sm active:scale-[0.98]"
            >
              Få pris nu
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Tjänster – expandable */}
          <button
            type="button"
            onClick={() => setServicesOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-[13px] font-semibold text-white/70 hover:text-white hover:bg-white/[0.07] transition-all"
          >
            Tjänster
            <ChevronDown className={`h-4 w-4 text-white/40 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
          </button>

          {servicesOpen && (
            <div className="ml-2 pl-3 border-l border-white/[0.08] space-y-0.5 py-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => go(cat.route)}
                  className={`
                    w-full text-left px-3 py-2.5 rounded-lg text-[13px] transition-all
                    ${currentPath === cat.route
                      ? 'text-[#f5f2e9] font-semibold bg-[#374ef4]/10'
                      : 'text-white/55 hover:text-white hover:bg-white/[0.06]'
                    }
                  `}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          <div className="pt-2 mt-2 border-t border-white/[0.07] space-y-0.5">
            {MOBILE_LINKS.map((item, i) => {
              if (item.external) {
                return (
                  <a
                    key={i}
                    href={item.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    className="flex items-center justify-between px-3.5 py-3 rounded-xl text-[13px] font-medium text-[#f5f2e9] hover:bg-[#374ef4]/10 transition-all"
                  >
                    {item.label}
                    <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                  </a>
                );
              }
              const active = currentPath === item.path;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(item.path)}
                  className={`
                    w-full text-left px-3.5 py-3 rounded-xl text-[13px] font-medium transition-all
                    ${active
                      ? 'text-white bg-white/10'
                      : 'text-white/60 hover:text-white hover:bg-white/[0.07]'
                    }
                  `}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/[0.07]">
          <p className="text-[11px] text-white/25 text-center">
            Klart Hemma AB
          </p>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Main Navbar
───────────────────────────────────────────────────────────────── */
export default function AppMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <>
      <header
        className={`kh-nav sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'kh-nav--scrolled' : ''}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[60px] flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="shrink-0 flex items-center">
            <BrandLogo />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-2">
            <ServicesDropdown navigate={navigate} currentPath={location.pathname} />
            <Link className="kh-nav-link rounded-lg px-3.5 py-2" to="/om-oss">Om oss</Link>
            <Link className="kh-nav-link rounded-lg px-3.5 py-2" to="/kontakt">Kontakt</Link>
          </nav>

          {/* Right: CTA + trust + hamburger */}
          <div className="flex items-center gap-3">

            {/* CTA group */}
            <div className="hidden sm:flex">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="kh-button px-5 py-2 text-sm hover:scale-[1.02] active:scale-[0.97]"
              >
                <Zap className="h-3.5 w-3.5 shrink-0" />
                Få pris
              </button>
            </div>

            {/* Mobile CTA (always visible, compact) */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="kh-button sm:hidden px-4 py-2 text-xs active:scale-95"
            >
              Få pris
            </button>

            {/* Hamburger */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Öppna meny"
              className="
                md:hidden
                h-9 w-9 flex items-center justify-center rounded-xl
                text-white/60 hover:text-white hover:bg-white/10
                transition-all duration-200
              "
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        navigate={navigate}
        currentPath={location.pathname}
      />
    </>
  );
}
